import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * VELVET — Vintage Italian Cinema / Theater
 *
 * Mood: 1960s Italian film palazzo. Burgundy + gold + cream. Theater
 * curtain reveals, intertitle cards, engraved/embossed type, wax seal date,
 * tinted vignettes.
 *
 * Premium techniques:
 *  - Theater-curtain clip-path reveals (two halves pull apart)
 *  - Intertitle cards between sections (like silent film title cards)
 *  - Engraved text effect (text-stroke + drop-shadow + transform)
 *  - Wax-seal animated date stamp (CSS clip-path + radial gradient + rotate)
 *  - Film grain noise overlay (SVG turbulence)
 *  - Slow tracking ken-burns on images
 *  - Art-deco ornaments
 */
export function VelvetLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate() ?? "—";
  const monthName = d?.toLocaleString("tr-TR", { month: "long" }).toUpperCase() ?? "";
  const year = d?.getFullYear() ?? "—";

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 2.0, easing: (t) => 1 - Math.pow(1 - t, 4) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── HERO: theater curtains open ────────────────────────────────
      gsap.to(".vlv-curtain-left", {
        x: "-100%",
        duration: 2.4,
        ease: "power3.inOut",
        delay: 0.5,
      });
      gsap.to(".vlv-curtain-right", {
        x: "100%",
        duration: 2.4,
        ease: "power3.inOut",
        delay: 0.5,
      });

      // Hero title rises after curtain
      const heroSplit = new SplitText(".vlv-hero-title", { type: "chars" });
      gsap.from(heroSplit.chars, {
        opacity: 0,
        y: 40,
        stagger: 0.05,
        duration: 1.4,
        ease: "expo.out",
        delay: 2.0,
      });

      gsap.from(".vlv-hero-sub", { opacity: 0, y: 20, duration: 1, delay: 2.6, ease: "power2.out" });
      gsap.from(".vlv-hero-ornament", { opacity: 0, scale: 0.8, duration: 1, delay: 2.8, ease: "back.out(1.5)" });

      // ── INTERTITLE CARDS — curtain-like wipe ───────────────────────
      document.querySelectorAll(".vlv-intertitle").forEach((card) => {
        gsap.from(card, {
          clipPath: "inset(50% 0 50% 0)",
          opacity: 0,
          duration: 1.4,
          ease: "power3.inOut",
          scrollTrigger: { trigger: card, start: "top 75%", toggleActions: "play reverse play reverse" },
        });

        const text = card.querySelector(".vlv-intertitle-text");
        if (text) {
          gsap.from(text, {
            opacity: 0,
            y: 20,
            duration: 1,
            delay: 0.3,
            ease: "power2.out",
            scrollTrigger: { trigger: card, start: "top 70%", toggleActions: "play reverse play reverse" },
          });
        }
      });

      // ── DATE WAX SEAL — rotate + scale ────────────────────────────
      gsap.from(".vlv-seal", {
        scale: 0,
        rotate: -180,
        opacity: 0,
        duration: 1.6,
        ease: "back.out(1.4)",
        scrollTrigger: { trigger: ".vlv-date-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });

      gsap.from(".vlv-date-meta", {
        opacity: 0,
        y: 30,
        stagger: 0.15,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".vlv-date-section", start: "top 55%", toggleActions: "play reverse play reverse" },
      });

      // ── STORY: each line fades in like film subtitles ─────────────
      document.querySelectorAll(".vlv-story-line").forEach((line) => {
        gsap.from(line, {
          opacity: 0,
          y: 20,
          duration: 1.2,
          ease: "power2.out",
          scrollTrigger: { trigger: line, start: "top 80%", toggleActions: "play reverse play reverse" },
        });
      });

      // ── KEN-BURNS on image panel ──────────────────────────────────
      gsap.to(".vlv-ken-burns", {
        scale: 1.15,
        ease: "none",
        scrollTrigger: {
          trigger: ".vlv-image-panel",
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });

      // ── VENUE engraved card ───────────────────────────────────────
      gsap.from(".vlv-venue-card", {
        opacity: 0,
        y: 50,
        duration: 1.4,
        ease: "power3.out",
        scrollTrigger: { trigger: ".vlv-venue-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      // ── FORM ──────────────────────────────────────────────────────
      gsap.from(".vlv-form-wrapper", {
        opacity: 0,
        scale: 0.95,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: { trigger: ".vlv-form-wrapper", start: "top 80%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  const storyLines = (description ?? "").split(/(?<=[.!?])\s+/).filter(Boolean);

  return (
    <main className="relative overflow-x-hidden" style={{ background: "#1a0a0c", color: "#f0d9b8", fontFamily: "'Playfair Display', Georgia, serif" }}>
      {/* ═══ FILM GRAIN OVERLAY (entire page) ════════════════════════ */}
      <svg className="fixed inset-0 w-full h-full pointer-events-none z-50 opacity-[0.06] mix-blend-overlay">
        <filter id="vlv-noise">
          <feTurbulence type="fractalNoise" baseFrequency="2.2" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#vlv-noise)" />
      </svg>

      {/* ═══ HERO — theater curtain reveal ═══════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background image (visible after curtains open) */}
        <div className="absolute inset-0">
          <img src={galleryImages[0]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.3) saturate(1.2) brightness(0.6)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 30%, #1a0a0c 100%)" }} />
        </div>

        {/* Left curtain */}
        <div
          className="vlv-curtain-left absolute top-0 left-0 w-1/2 h-full z-30"
          style={{
            background: "linear-gradient(90deg, #2a0608 0%, #4a0a14 50%, #2a0608 100%)",
            boxShadow: "inset -20px 0 50px rgba(0,0,0,0.6)",
            backgroundImage: "repeating-linear-gradient(90deg, transparent 0, transparent 14px, rgba(0,0,0,0.15) 14px, rgba(0,0,0,0.15) 28px)",
          }}
        />
        {/* Right curtain */}
        <div
          className="vlv-curtain-right absolute top-0 right-0 w-1/2 h-full z-30"
          style={{
            background: "linear-gradient(-90deg, #2a0608 0%, #4a0a14 50%, #2a0608 100%)",
            boxShadow: "inset 20px 0 50px rgba(0,0,0,0.6)",
            backgroundImage: "repeating-linear-gradient(90deg, transparent 0, transparent 14px, rgba(0,0,0,0.15) 14px, rgba(0,0,0,0.15) 28px)",
          }}
        />

        {/* Letterbox bars (cinematic ratio) */}
        <div className="absolute top-0 left-0 right-0 h-[8vh] bg-black z-20 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-[8vh] bg-black z-20 pointer-events-none" />

        {/* Hero content */}
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <p className="vlv-hero-sub text-[10px] uppercase tracking-[0.6em] text-[#c9a96e] mb-10">
            — Un Film d'Amore —
          </p>
          <h1
            className="vlv-hero-title font-light leading-[0.9]"
            style={{
              fontSize: "clamp(3.5rem, 11vw, 9rem)",
              color: "#f0d9b8",
              textShadow: "0 2px 0 rgba(0,0,0,0.5), 0 0 80px rgba(201,169,110,0.3)",
            }}
          >
            {title}
          </h1>

          {/* Art-deco ornament */}
          <div className="vlv-hero-ornament mt-10 flex items-center justify-center gap-4">
            <svg width="80" height="16" viewBox="0 0 80 16">
              <line x1="0" y1="8" x2="30" y2="8" stroke="#c9a96e" strokeWidth="0.5" />
              <polygon points="35,8 40,2 45,8 40,14" fill="#c9a96e" opacity="0.8" />
              <line x1="50" y1="8" x2="80" y2="8" stroke="#c9a96e" strokeWidth="0.5" />
            </svg>
          </div>

          <p className="vlv-hero-sub mt-6 text-xs tracking-[0.4em] uppercase text-[#c9a96e]/70">
            {monthName} · {year}
          </p>
        </div>
      </section>

      {/* ═══ INTERTITLE 1 ════════════════════════════════════════════ */}
      <section className="relative py-32 px-6 bg-[#0a0306]">
        <div className="vlv-intertitle max-w-2xl mx-auto border border-[#c9a96e]/40 p-12 text-center" style={{ background: "linear-gradient(135deg, #1a0808 0%, #2a0c0e 100%)" }}>
          <p className="text-[10px] uppercase tracking-[0.6em] text-[#c9a96e]/60 mb-6">— Atto Primo —</p>
          <p className="vlv-intertitle-text italic font-light leading-tight" style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", color: "#f0d9b8" }}>
            "Iki ruh tek bir kalp bulduğu zaman, başlar her şey."
          </p>
        </div>
      </section>

      {/* ═══ DATE — WAX SEAL ═════════════════════════════════════════ */}
      <section className="vlv-date-section relative min-h-screen flex items-center justify-center px-6 py-32">
        <div className="text-center">
          <p className="vlv-date-meta text-[10px] uppercase tracking-[0.6em] text-[#c9a96e]/60 mb-12">— La Data —</p>

          {/* Wax seal */}
          <div className="relative mx-auto mb-12" style={{ width: 280, height: 280 }}>
            <div
              className="vlv-seal absolute inset-0 flex flex-col items-center justify-center"
              style={{
                background: "radial-gradient(circle at 30% 30%, #6a1018 0%, #3a0408 80%)",
                borderRadius: "50%",
                boxShadow: "inset 0 0 60px rgba(0,0,0,0.5), inset 0 4px 10px rgba(255,200,180,0.2), 0 10px 40px rgba(0,0,0,0.5)",
                clipPath: "polygon(50% 0%, 60% 5%, 70% 2%, 78% 10%, 88% 12%, 92% 22%, 100% 28%, 98% 40%, 102% 50%, 98% 60%, 100% 72%, 92% 78%, 88% 88%, 78% 90%, 70% 98%, 60% 95%, 50% 100%, 40% 95%, 30% 98%, 22% 90%, 12% 88%, 8% 78%, 0% 72%, 2% 60%, -2% 50%, 2% 40%, 0% 28%, 8% 22%, 12% 12%, 22% 10%, 30% 2%, 40% 5%)",
              }}
            >
              <p className="text-[#c9a96e] text-xs uppercase tracking-[0.4em] mb-1">{monthName}</p>
              <p className="font-light leading-none" style={{ color: "#f0d9b8", fontSize: 96, fontFamily: "'Playfair Display', serif", textShadow: "0 2px 4px rgba(0,0,0,0.4)" }}>{day}</p>
              <p className="text-[#c9a96e] text-xs uppercase tracking-[0.4em] mt-1">{year}</p>
            </div>
          </div>

          <p className="vlv-date-meta italic text-lg tracking-wide text-[#f0d9b8]/70 mb-3">
            {d?.toLocaleDateString("tr-TR", { weekday: "long" })}
          </p>
          <p className="vlv-date-meta text-xs tracking-[0.4em] uppercase text-[#c9a96e]/50">
            ore {d?.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
      </section>

      {/* ═══ IMAGE PANEL + KEN-BURNS ═════════════════════════════════ */}
      <section className="vlv-image-panel relative h-[90vh] overflow-hidden">
        <img src={galleryImages[1]} alt="" className="vlv-ken-burns w-full h-full object-cover" style={{ filter: "sepia(0.4) saturate(1.3) brightness(0.7)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0a0c] via-transparent to-[#1a0a0c]" />

        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <p className="italic font-light text-center text-[#f0d9b8] tracking-tight leading-tight" style={{ fontSize: "clamp(1.5rem, 4vw, 3.5rem)" }}>
            "L'amore è una vita intera<br />in un solo respiro."
          </p>
        </div>
      </section>

      {/* ═══ INTERTITLE 2 — STORY ════════════════════════════════════ */}
      <section className="relative py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] text-[#c9a96e]/60 mb-16">— Atto Secondo · La Nostra Storia —</p>
          {storyLines.map((line, i) => (
            <p
              key={i}
              className="vlv-story-line italic font-light text-center leading-relaxed mb-5"
              style={{
                fontSize: "clamp(1.1rem, 1.8vw, 1.5rem)",
                color: "#f0d9b8",
                opacity: 0.85,
              }}
            >
              "{line.trim()}"
            </p>
          ))}
          <div className="text-center mt-12">
            <svg width="100" height="24" viewBox="0 0 100 24" className="inline-block">
              <line x1="0" y1="12" x2="35" y2="12" stroke="#c9a96e" strokeWidth="0.5" opacity="0.6" />
              <circle cx="50" cy="12" r="3" fill="#c9a96e" />
              <line x1="65" y1="12" x2="100" y2="12" stroke="#c9a96e" strokeWidth="0.5" opacity="0.6" />
            </svg>
          </div>
        </div>
      </section>

      {/* ═══ VENUE — ENGRAVED CARD ═══════════════════════════════════ */}
      <section className="vlv-venue-section relative py-32 px-6">
        <div className="vlv-venue-card max-w-2xl mx-auto border-2 border-[#c9a96e]/30 p-12 sm:p-16 text-center" style={{ background: "radial-gradient(ellipse at center, #2a0c0e 0%, #1a0608 100%)" }}>
          {/* Corner ornaments */}
          <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-[#c9a96e]/40" />
          <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-[#c9a96e]/40" />
          <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-[#c9a96e]/40" />
          <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-[#c9a96e]/40" />

          <p className="text-[10px] uppercase tracking-[0.6em] text-[#c9a96e]/60 mb-8">— Il Luogo —</p>
          <h2
            className="italic font-light leading-tight mb-8"
            style={{
              fontSize: "clamp(2rem, 5vw, 4rem)",
              color: "#f0d9b8",
              textShadow: "0 1px 0 rgba(0,0,0,0.3)",
            }}
          >
            {location ?? "—"}
          </h2>
          <p className="text-xs uppercase tracking-[0.4em] text-[#c9a96e]/70">
            {d?.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
      </section>

      {/* ═══ FORM ════════════════════════════════════════════════════ */}
      <section className="relative py-32 px-6">
        <div className="vlv-form-wrapper max-w-md mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] text-[#c9a96e]/60 mb-4">— Risposta —</p>
          <h3 className="text-center italic font-light text-3xl text-[#f0d9b8] mb-10">
            Onaylayın
          </h3>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
